/**
 * First-touch attribution capture.
 *
 * When a visitor lands on the site we snapshot marketing parameters and the
 * landing page. These are held in sessionStorage so any subsequent form
 * submission during the same visit can be attributed correctly, without ever
 * putting the values in the URL of the thank-you redirect.
 *
 * No PHI, no PII — only Google click IDs, UTM params, landing URL, referrer.
 */
const ATTR_KEY = "vv_attribution";
const CONF_KEY = "vv_last_confirmation";
const FORM_KEY = "submitted_form_name";

const ATTR_PARAMS = [
  "gclid", "gbraid", "wbraid",
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "msclkid", "fbclid",
];

const IS_DEV = typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production";

function safeGet(key) {
  try { return sessionStorage.getItem(key); } catch (err) {
    if (IS_DEV) console.warn(`[attribution] sessionStorage.getItem(${key}) failed`, err);
    return null;
  }
}
function safeSet(key, value) {
  try { sessionStorage.setItem(key, value); } catch (err) {
    if (IS_DEV) console.warn(`[attribution] sessionStorage.setItem(${key}) failed`, err);
  }
}
function safeRemove(key) {
  try { sessionStorage.removeItem(key); } catch (err) {
    if (IS_DEV) console.warn(`[attribution] sessionStorage.removeItem(${key}) failed`, err);
  }
}

/**
 * Idempotent: safe to call on every route change. Only overwrites values when
 * the current URL actually carries a new attribution param (first-touch wins
 * for landing_url + referrer, latest-touch wins for click IDs).
 */
export function captureAttribution() {
  if (typeof window === "undefined") return;

  let current = {};
  try { current = JSON.parse(safeGet(ATTR_KEY) || "{}"); } catch (_) { current = {}; }

  const url = new URL(window.location.href);
  const incoming = {};
  ATTR_PARAMS.forEach((p) => {
    const v = url.searchParams.get(p);
    if (v) incoming[p] = v;
  });

  // Landing URL & referrer: first-touch only.
  if (!current.landing_url) current.landing_url = window.location.href;
  if (!current.referrer_url && document.referrer) current.referrer_url = document.referrer;

  // Click IDs / UTM: latest-touch overwrite.
  Object.assign(current, incoming);

  safeSet(ATTR_KEY, JSON.stringify(current));
  return current;
}

/**
 * Return attribution values suitable for embedding in a lead payload.
 * Callers should merge these into the form body just before POST.
 */
export function getAttribution() {
  if (typeof window === "undefined") return {};
  try { return JSON.parse(safeGet(ATTR_KEY) || "{}"); } catch (_) { return {}; }
}

/** Save the confirmation number + form identifier just before redirect. */
export function stashConfirmation({ confirmation, formName }) {
  if (confirmation) safeSet(CONF_KEY, confirmation);
  safeSet(FORM_KEY, formName || "website_lead_form");
}

export function readStashedConfirmation() {
  return {
    confirmation: safeGet(CONF_KEY) || "",
    formName: safeGet(FORM_KEY) || "website_lead_form",
  };
}

export function clearStashedConfirmation() {
  safeRemove(CONF_KEY);
  // Deliberately keep the form_name so a page refresh still knows what
  // was submitted; the confirmation number is cleared so re-submissions
  // don't accidentally reuse it.
}
